<template>
  <div v-if="hasOtherPlatforms" class="switch-platform">
    <button type="button" class="switch-platform__trigger" :aria-expanded="isOpen" @click="toggle">
      Switch Platform
      <svg
        class="switch-platform__chevron"
        :class="{ 'switch-platform__chevron--open': isOpen }"
        width="10"
        height="6"
        viewBox="0 0 10 6"
        fill="none"
      >
        <path d="M1 1L5 5L9 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>
    <div v-if="isOpen" class="switch-platform__menu" @click.stop>
      <div
        v-for="option in options"
        :key="option.key"
        class="switch-platform__item"
        :class="{ 'switch-platform__item--current': isCurrent(option) }"
        :role="isCurrent(option) ? undefined : 'button'"
        :tabindex="isCurrent(option) ? undefined : 0"
        @click="select(option)"
        @keydown.enter="select(option)"
      >
        <div class="switch-platform__option">{{ option.label }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { SWITCH_PLATFORM_OPTIONS, getSwitchPlatformUrl } from "@/utils/switchPlatform";
import { getTokenForPlatformHandoff } from "@/utils/sessionManager";
import { isUnimodAccessAllowed } from "@/utils/unimodAccess";
import { isVaPortalAccessAllowed } from "@/utils/vaPortalAccess";

// This copy of the menu lives in ehr-frontend (Vibrant Intelligence), so it's always the
// current platform.
const CURRENT_PLATFORM = "vibrant-intelligence";

export default {
  name: "SwitchPlatformMenu",
  data() {
    return {
      isOpen: false,
      isSwitching: false,
    };
  },
  computed: {
    userInfo() {
      return this.$store.state.login.userInfo;
    },
    // Unimod is gated by hasAdminPanelAccess (backend-controlled via userInfo), and
    // Vibrant Portal is off-limits to sales-role internal users — don't offer switching
    // to a portal this user isn't allowed into.
    options() {
      return SWITCH_PLATFORM_OPTIONS.filter((option) => {
        if (option.key === "unimod") {
          return isUnimodAccessAllowed(this.userInfo);
        }
        if (option.key === "vibrant-portal") {
          return isVaPortalAccessAllowed(this.userInfo);
        }
        return true;
      });
    },
    // Nothing to switch to — every option besides the current platform got filtered out —
    // so there's no point showing the menu at all.
    hasOtherPlatforms() {
      return this.options.length > 1;
    },
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeOnClickOutside);
  },
  methods: {
    isCurrent(option) {
      return option.key === CURRENT_PLATFORM;
    },
    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        document.addEventListener("click", this.closeOnClickOutside);
      } else {
        document.removeEventListener("click", this.closeOnClickOutside);
      }
    },
    closeOnClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
        document.removeEventListener("click", this.closeOnClickOutside);
      }
    },
    async select(option) {
      if (this.isCurrent(option) || this.isSwitching) return;
      // Force-refresh before handing the token to the other platform, which validates it
      // ahead of its first API call — see getTokenForPlatformHandoff. The guard above keeps
      // a second click during that round trip from opening a duplicate tab / navigation.
      this.isSwitching = true;
      const token = await getTokenForPlatformHandoff();
      this.isSwitching = false;
      if (token) {
        const url = getSwitchPlatformUrl(option.key, token);
        if (url) {
          if (option.newTab) {
            window.open(url, "_blank");
          } else {
            window.location.href = url;
          }
        }
      }
      this.isOpen = false;
      document.removeEventListener("click", this.closeOnClickOutside);
    },
  },
};
</script>

<style scoped>
.switch-platform {
  position: relative;
}

.switch-platform__trigger {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: #1e4670;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  text-decoration-line: underline;
}

.switch-platform__chevron {
  transition: transform 0.2s ease;
}

.switch-platform__chevron--open {
  transform: rotate(180deg);
}

.switch-platform__menu {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  z-index: 20;
  width: 286px;
  background: #fff;
  border: 1px solid #c4c4c4;
  border-radius: 0 0 4px 4px;
  padding: 6px 1px;
}

.switch-platform__item {
  padding: 6px 12px;
  cursor: pointer;
}

.switch-platform__item--current {
  cursor: default;
}

/* The highlight only wraps the text (line-height, 4px inset) — not the full
   6px/12px-padded row — matching the Figma "Single_Option" box. */
.switch-platform__option {
  padding: 0 6px;
  border-radius: 4px;
  color: #333;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.15px;
}

.switch-platform__item--current .switch-platform__option {
  background: rgba(30, 70, 112, 0.12);
}

.switch-platform__item:not(.switch-platform__item--current):hover .switch-platform__option {
  color: #004879;
  background: rgba(0, 72, 121, 0.08);
}
</style>
